import { useEffect } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  cmd?: boolean;
  shift?: boolean;
  alt?: boolean;
  handler: () => void;
  description: string;
}

/**
 * Hook for registering global keyboard shortcuts
 * Handles Cmd (Mac) vs Ctrl (Windows/Linux) automatically
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

      for (const shortcut of shortcuts) {
        const metaKey = shortcut.cmd || shortcut.ctrl;
        const needsMeta = metaKey !== undefined;
        const needsShift = shortcut.shift || false;
        const needsAlt = shortcut.alt || false;

        // Check if the shortcut matches
        const metaPressed = isMac ? event.metaKey : event.ctrlKey;
        const shiftPressed = event.shiftKey;
        const altPressed = event.altKey;

        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const metaMatches = !needsMeta || (needsMeta && metaPressed);
        const shiftMatches = needsShift === shiftPressed;
        const altMatches = needsAlt === altPressed;

        if (keyMatches && metaMatches && shiftMatches && altMatches) {
          // Prevent default browser behavior
          event.preventDefault();
          event.stopPropagation();

          // Execute the handler
          shortcut.handler();
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcuts]);
};

/**
 * Format shortcut for display
 * Automatically shows Cmd or Ctrl based on platform
 */
export const formatShortcut = (shortcut: Omit<KeyboardShortcut, 'handler'>): string => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const parts: string[] = [];

  if (shortcut.ctrl || shortcut.cmd) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  parts.push(shortcut.key.toUpperCase());

  return parts.join('+');
};
